import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import {
	CForm, CFormLabel, CFormTextarea, CButton, CRow, CCol,
	CCard, CCardHeader, CCardBody, CFormCheck,
} from '@coreui/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Select from 'react-select';
import { CenterLoader } from 'src/components/myComponents/CenterLoader';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { useSupportCategories, useInactiveSupportCategories } from 'src/hooks/useSupportCategories';

const urlApi = '/qa';
const urlRouter = '/qa';

const formatDateTime = (val) => {
	if (!val) return '—';
	const d = new Date(val);
	return d.toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });
};

const formatUser = (user) => {
	if (!user) return '—';
	const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
	return name || user.email || user.username || String(user.id);
};

const QaForm = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isCreateMode = !id;

	const activeCategories = useSupportCategories();
	const allInactiveCategories = useInactiveSupportCategories();

	const [isEdit, setIsEdit] = useState(isCreateMode || location.pathname.includes('/update/'));
	useEffect(() => {
		setIsEdit(isCreateMode || location.pathname.includes('/update/'));
	}, [location.pathname, isCreateMode]);

	const [data, setData] = useState(null);
	const [question, setQuestion] = useState('');
	const [answer, setAnswer] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [selectedCategories, setSelectedCategories] = useState([]);     // тільки активні (для react-select)
	const [inactiveAttachedCategories, setInactiveAttachedCategories] = useState([]); // прив'язані але неактивні
	const [loading, setLoading] = useState(!!id);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});

	// Опції для react-select: тільки активні з scope knowledge_base або all
	const categoryOptions = activeCategories
		.filter(c => c.scopes?.some(s => s === 'knowledge_base' || s === 'all'))
		.map(c => ({ value: c.id, label: c.name }));

	useEffect(() => {
		if (!id) return;
		setLoading(true);
		AxiosInstance.get(`${urlApi}/${id}`)
			.then(res => {
				const item = res.data;
				setData(item);
				setQuestion(item.question || '');
				setAnswer(item.answer || '');
				setIsActive(item.is_active ?? true);
				// Спочатку ставимо всі категорії — useEffect нижче розділить на активні/неактивні
				setSelectedCategories((item.categories || []).map(c => ({ value: c.id, label: c.name })));
			})
			.catch(() => toast.error('Помилка завантаження'))
			.finally(() => setLoading(false));
	}, [id]);

	// Розділяємо прив'язані категорії на активні та неактивні після завантаження обох списків
	useEffect(() => {
		if (!data?.categories?.length || !activeCategories.length) return;
		const activeIdSet = new Set(activeCategories.map(c => c.id));
		const missingIds = data.categories.filter(c => !activeIdSet.has(c.id));
		if (!missingIds.length) {
			setInactiveAttachedCategories([]);
			return;
		}
		setInactiveAttachedCategories(
			missingIds.map(c => {
				// Намагаємось взяти свіжу назву з хуку неактивних
				const fromHook = allInactiveCategories.find(ic => ic.id === c.id);
				return fromHook ?? c;
			})
		);
		// Прибираємо неактивні зі стейту react-select
		setSelectedCategories(prev => prev.filter(c => activeIdSet.has(c.value)));
	}, [data, activeCategories, allInactiveCategories]);

	const getActiveCategoryIds = () => selectedCategories.map(c => c.value);
	const getInactiveCategoryIds = () => inactiveAttachedCategories.map(c => c.id);
	const getAllCategoryIds = () => [...getActiveCategoryIds(), ...getInactiveCategoryIds()];

	const getDataCategoryIds = () => (data?.categories || []).map(c => c.id);

	const hasChanges = !isCreateMode && data !== null && (
		question !== (data.question || '') ||
		answer !== (data.answer || '') ||
		isActive !== (data.is_active ?? true) ||
		[...getAllCategoryIds()].sort().join(',') !== [...getDataCategoryIds()].sort().join(',')
	);

	const handleReset = useCallback(() => {
		if (!data) return;
		setQuestion(data.question || '');
		setAnswer(data.answer || '');
		setIsActive(data.is_active ?? true);
		// Перерозподіляємо категорії за поточним станом активних
		const activeIdSet = new Set(activeCategories.map(c => c.id));
		setSelectedCategories(
			(data.categories || []).filter(c => activeIdSet.has(c.id)).map(c => ({ value: c.id, label: c.name }))
		);
		setInactiveAttachedCategories(
			(data.categories || []).filter(c => !activeIdSet.has(c.id)).map(c => {
				const fromHook = allInactiveCategories.find(ic => ic.id === c.id);
				return fromHook ?? c;
			})
		);
		setErrors({});
	}, [data, activeCategories, allInactiveCategories]);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const handleSubmit = async () => {
		const newErrors = {
			question: !question.trim(),
			answer: !answer.trim(),
		};
		if (newErrors.question || newErrors.answer) {
			setErrors(newErrors);
			const missing = [
				newErrors.question && 'Питання',
				newErrors.answer && 'Відповідь',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setErrors({});

		// created_by / updated_by встановлюються сервером через JWT — не передаємо у payload
		// Неактивні прив'язані категорії завжди зберігаємо (не втрачаємо зв'язок)
		const payload = {
			question: question.trim(),
			answer: answer.trim(),
			is_active: isActive,
			category_ids: getAllCategoryIds(),
		};

		if (isCreateMode) {
			try {
				const res = await AxiosInstance.post(urlApi, payload);
				toast.success('Запис створено!');
				navigate(`${urlRouter}/view/${res.data.id}`, { replace: true });
			} catch (err) {
				toast.error(err.response?.data?.error || 'Помилка збереження');
			}
			return;
		}

		setSubmitting(true);
		try {
			const res = await AxiosInstance.put(`${urlApi}/${id}`, payload);
			const item = res.data;
			setData(item);
			setQuestion(item.question || '');
			setAnswer(item.answer || '');
			setIsActive(item.is_active ?? true);
			setSelectedCategories((item.categories || []).map(c => ({ value: c.id, label: c.name })));
			toast.success('Оновлено!');
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження');
		} finally {
			setSubmitting(false);
		}
	};

	const entityName = 'Запис Q&A';
	const cardTitle = isCreateMode
		? 'Новий запис Q&A'
		: (isEdit ? `${entityName} — редагування` : `${entityName} — перегляд`);

	return (
		<>
			<ToastContainer />
			<CCard className="mb-4">
				<CCardHeader><strong>{cardTitle}</strong></CCardHeader>
				<CCardBody>
					{loading ? (
						<CenterLoader />
					) : (
						<FormSubmitOverlay submitting={submitting}>
							<CForm className="mb-3">
								{isEdit ? (
									<CRow className="g-3">
										<CCol md={12}>
											<CFormLabel>Питання *</CFormLabel>
											<CFormTextarea
												rows={3}
												value={question}
												onChange={(e) => {
													setQuestion(e.target.value);
													if (errors.question) setErrors(prev => ({ ...prev, question: false }));
												}}
												autoComplete="off"
												invalid={!!errors.question}
											/>
										</CCol>
										<CCol md={12}>
											<CFormLabel>Відповідь *</CFormLabel>
											<CFormTextarea
												rows={6}
												value={answer}
												onChange={(e) => {
													setAnswer(e.target.value);
													if (errors.answer) setErrors(prev => ({ ...prev, answer: false }));
												}}
												autoComplete="off"
												invalid={!!errors.answer}
											/>
										</CCol>
										<CCol md={8}>
											<CFormLabel>Категорії</CFormLabel>
											<Select
												isMulti
												options={categoryOptions}
												value={selectedCategories}
												onChange={setSelectedCategories}
												placeholder="Оберіть категорії..."
												isClearable
												classNamePrefix="react-select"
											/>
											{/* Неактивні прив'язані категорії — відображаємо перекресленими, зберігаємо в payload */}
											{inactiveAttachedCategories.map(c => (
												<CFormCheck
													key={c.id}
													id={`cat-inactive-${c.id}`}
													label={<><s>{c.name}</s>{' '}(неактивна)</>}
													checked
													disabled
												/>
											))}
										</CCol>
										<CCol md={4} className="d-flex align-items-end pb-1">
											<span title="Визначає, чи включається цей запис в вибірку для операторів і експорт до Google Sheets.">
												<CFormCheck
													id="isActive"
													label="Активна"
													checked={isActive}
													onChange={(e) => setIsActive(e.target.checked)}
												/>
											</span>
										</CCol>
									</CRow>
								) : (
									<CRow className="g-3">
										<CCol md={2}>
											<CFormLabel>ID</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '38px' }}>
												{data?.id}
											</div>
										</CCol>
										<CCol md={2}>
											<CFormLabel>Активна</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '38px' }}>
												{(data?.is_active ?? true) ? 'Так' : 'Ні'}
											</div>
										</CCol>
										<CCol md={4}>
											<CFormLabel>Створено</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '38px' }}>
												{formatUser(data?.created_by)} о {formatDateTime(data?.created_at)}
											</div>
										</CCol>
										<CCol md={4}>
											<CFormLabel>Оновлено</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '38px' }}>
												{formatUser(data?.updated_by)} о {formatDateTime(data?.updated_at)}
											</div>
										</CCol>
										<CCol md={12}>
											<CFormLabel>Питання</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '80px', whiteSpace: 'pre-wrap' }}>
												{question}
											</div>
										</CCol>
										<CCol md={12}>
											<CFormLabel>Відповідь</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '120px', whiteSpace: 'pre-wrap' }}>
												{answer}
											</div>
										</CCol>
										<CCol md={12}>
											<CFormLabel>Категорії</CFormLabel>
											<div className="form-control" style={{ background: '#e9ecef', minHeight: '38px' }}>
												{selectedCategories.length === 0 && inactiveAttachedCategories.length === 0
													? '—'
													: <>
														{selectedCategories.map((c, i) => (
															<React.Fragment key={c.value}>
																{i > 0 && ', '}
																{c.label}
															</React.Fragment>
														))}
														{inactiveAttachedCategories.map((c, i) => (
															<React.Fragment key={c.id}>
																{(selectedCategories.length > 0 || i > 0) && ', '}
																<s>{c.name}</s>{' '}(неактивна)
															</React.Fragment>
														))}
													</>
												}
											</div>
										</CCol>
									</CRow>
								)}

								<div className="d-flex justify-content-start mt-4">
									<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
										← Назад
									</CButton>
									{!isEdit && (
										<CButton color="success" className="ms-2" onClick={() => navigate(`${urlRouter}/update/${id}`)} title="Перейти до форми редагування">
											Редагувати
										</CButton>
									)}
									{isEdit && (
										<CButton color="success" className="ms-2" onClick={handleSubmit} disabled={!isCreateMode && !hasChanges} title={isCreateMode ? 'Створити новий запис' : 'Зберегти зміни'}>
											{isCreateMode ? 'Створити' : 'Зберегти'}
										</CButton>
									)}
									{isEdit && !isCreateMode && (
										<CButton color="secondary" variant="outline" className="ms-2" onClick={handleReset} disabled={!hasChanges} title="Скинути форму до останніх збережених значень">
											Скинути
										</CButton>
									)}
									{isEdit && !isCreateMode && (
										<CButton color="danger" variant="outline" className="ms-2" onClick={() => navigate(`${urlRouter}/view/${id}`)} title="Скасувати редагування і повернутись до перегляду">
											До перегляду
										</CButton>
									)}
								</div>
							</CForm>
						</FormSubmitOverlay>
					)}
				</CCardBody>
			</CCard>
		</>
	);
};

export default QaForm;
